<script setup>
import { ref, reactive, onMounted, computed } from "vue";
import { useTheme } from "vuetify";
import AppsList from "./components/AppsList.vue";
import { appBarStore } from "@/stores/appBar";
import { apiQuery } from "@/api/j24api";
import AlertsList from "@/components/AlertsList.vue";
import UpdatesBehaviour from "./components/UpdatesBehaviour.vue";
import PatchingPolicyGroups from "./PatchingPolicyGroups.vue";
import NotificationSettings from "./components/NotificationSettings.vue";
import AppRules from "./components/AppRules.vue";
import validateConfiguration from "./components/validateConfiguration";
import { permissionsStore } from "@/stores/permissions";
const permissions = permissionsStore();

const appBar = appBarStore();

const theme = useTheme();
const alerts = ref([]);
const apps = reactive([]);

const selectedTab = ref("Global");
const globalConfiguration = ref({ PatchAllApps: true, AppRules: [] });
const configurations = reactive([]);
const loading = ref(false);

const showSections = ref({
  UpdateBehaviour: true,
  Notifications: true,
});

onMounted(() => {
  theme.global.name.value = "jigsawNew";
  getAllApps();
  if (appBar.selected) {
    getLicense(appBar.selected);
  }
});

const selectedLicense = ref({});
appBar.$subscribe(() => {
  console.log("Appbar changed", appBar.$state);
  if (appBar.selected) {
    getLicense(appBar.selected);
    getAllApps();
  }
});

const readOnlyMode = computed(() => {
  let result =
    permissions.$state &&
    permissions.$state["PatchingV2-Configuration"] &&
    permissions.$state["PatchingV2-Configuration"].actions.includes("Update");
  return !result;
});

// function hasPermission(permissionKey, action = null) {
//   console.log(
//     "Checking permission for key:",
//     permissionKey,
//     permissions.$state
//   );
//   if (action) {
//     console.log("With action:", action);
//     // Find permission with action
//     let result =
//       permissions.$state &&
//       permissions.$state[permissionKey] &&
//       permissions.$state[permissionKey].actions.includes(action);
//     console.log("Permission result:", result);
//     return result;
//   }
//   let result = permissions.$state && permissions.$state[permissionKey];
//   console.log("Permission result:", result);
//   return result;
// }

function checkForUnsetValues(config) {
  if (!config || typeof config !== "object") {
    config = {};
  }
  let defaults = {
    UpdateDeadline: 14,
    GracePeriod: 6,
    PatchingDelay: 2,
    EnableNotifications: true,
    NotificationFrequency: 3,
    NotificationFrequencyPostDeadline: 30,
    PatchAllApps: true,
    ExcludedApps: [],
    //EnabledApps: [],
    AppsSelected: [],
    AppRules: [],
  };
  for (let key in defaults) {
    // Only set default when the value is truly undefined or null.
    // This preserves false, 0, empty string and empty arrays.
    if (config[key] === undefined || config[key] === null) {
      config[key] = defaults[key];
    }
  }
  return config;
}

async function saveConfiguration() {
  try {
    let validationErrors = validateConfiguration(globalConfiguration.value);
    console.log("Validation Errors:", validationErrors);
    if (validationErrors.errors.length) {
      alerts.value.push(...validationErrors.errors);
      return;
    }
    var result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "savePatchingConfig",
      id: selectedLicense.value._id,
      GlobalConfiguration: globalConfiguration.value,
    });
    if (result) {
      alerts.value.push({
        type: "success",
        message: "Configuration Saved",
        closable: true,
      });
    } else {
      alerts.value.push({
        type: "error",
        message: "Error saving configuration",
        closable: true,
      });
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error saving configuration",
      closable: true,
    });
  }
}

async function getLicense(id) {
  loading.value = true;
  try {
    var result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getPatchingLicense",
      id: id,
    });
    if (result.length) {
      selectedLicense.value = result[0];
      if (!result[0].GlobalConfiguration) {
        result[0].GlobalConfiguration = {};
      }
      globalConfiguration.value = checkForUnsetValues(
        result[0].GlobalConfiguration
      );
      if (result[0].Configurations?.length) {
        configurations.length = 0;
        configurations.push(...result[0].Configurations);
      } else {
        configurations.length = 0;
      }
    } else {
      alerts.value.push({
        type: "error",
        message: "Error getting license",
        closable: true,
      });
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting license",
      closable: true,
    });
  }
  loading.value = false;
}

async function getAllApps() {
  const result = await apiQuery("ELA", "patching/patchingManagement", {
    action: "getAppsList",
  });
  if (result.length) {
    apps.length = 0;
    apps.push(...result);
  }
}
</script>

<template>
  <template v-if="appBar.selected">
    <h1>Patch Policy</h1>
    <v-row class="mb-3">
      <v-col cols="auto">
        <v-tabs color="#e7541e" v-model="selectedTab">
          <v-tab value="Global">Global Policy</v-tab>
          <v-tab value="Group">Configuration Groups</v-tab>
        </v-tabs>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto">
        <v-btn
          v-if="!readOnlyMode && selectedTab == 'Global'"
          color="primary"
          @click="saveConfiguration()"
          class="mr-1"
        >
          Save
        </v-btn>
      </v-col>
    </v-row>

    <AlertsList v-model="alerts" />
    <v-window v-model="selectedTab">
      <v-window-item value="Global">
        <v-row classz="mb-5">
          <v-col cols="6">
            <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5">
              <UpdatesBehaviour
                v-model="globalConfiguration"
                :subConfig="false"
                :loading="loading"
                :readOnlyMode="readOnlyMode"
              />
            </v-sheet>
          </v-col>
          <v-col cols="6">
            <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5 mr-1">
              <NotificationSettings
                v-model="globalConfiguration"
                :subConfig="false"
                :loading="loading"
                :readOnlyMode="readOnlyMode"
              />
            </v-sheet>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <h2>Applications</h2>
          </v-col>
        </v-row>

        <v-row classz="mb-5">
          <v-col cols="6">
            <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5">
              <v-row>
                <v-col cols="6">
                  <div class="font-weight-bold">Patch all applications</div>
                  <div
                    class="text-body-2 font-weight-light text-medium-emphasis"
                  >
                    Every app from the Jigsaw24 app list will be patched on
                    devices
                  </div>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto">
                  <v-switch
                    :loading="loading"
                    :label="globalConfiguration.PatchAllApps ? 'On' : 'Off'"
                    color="primary"
                    baseColor="grey-darken-2"
                    v-model="globalConfiguration.PatchAllApps"
                    :readonly="readOnlyMode"
                  ></v-switch>
                </v-col>
              </v-row>
            </v-sheet>
          </v-col>
        </v-row>

        <v-row
          class="mb-5"
          v-if="
            globalConfiguration.PatchAllApps === true &&
            globalConfiguration.ExcludedApps
          "
        >
          <v-col cols="12">
            <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5 mr-1">
              <AppsList
                :apps="apps"
                title="Excluded Apps"
                description="Exclude applications from being patched from the Patch24 catalogue."
                v-model="globalConfiguration.ExcludedApps"
                :readOnlyMode="readOnlyMode"
              />
            </v-sheet>
          </v-col>
        </v-row>

        <v-row class="mb-5" v-if="globalConfiguration.PatchAllApps === false">
          <v-col cols="12">
            <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5 mr-1">
              <AppsList
                :apps="apps"
                title="Apps to Patch"
                v-model="globalConfiguration.AppsSelected"
                :readOnlyMode="readOnlyMode"
              />
            </v-sheet>
          </v-col>
        </v-row>

        <v-row class="mb-5">
          <v-col cols="12">
            <v-sheet rounded="lg" elevation="2" height="100%" class="pa-5 mr-1">
              <AppRules
                :apps="apps"
                v-model:rules="globalConfiguration.AppRules"
                :readOnlyMode="readOnlyMode"
              />
            </v-sheet>
          </v-col>
        </v-row>
      </v-window-item>
      <v-window-item value="Group">
        <PatchingPolicyGroups
          v-model="configurations"
          :globalConfig="globalConfiguration"
          :apps="apps"
          :licenseId="appBar.selected"
          :readOnlyMode="readOnlyMode"
          @saved="getLicense(appBar.selected)"
        />
      </v-window-item>
    </v-window>
  </template>
  <template v-else>
    <v-row>
      <v-col>
        <h1 v-if="appBar.items.length > 1">Select Patching License Above</h1>
        <h1 v-else>Loading please wait...</h1>
      </v-col>
    </v-row>
  </template>
</template>
